import { Badge } from '@/components/ui/badge';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';

interface InstallmentStatusBadgeProps {
    status: IInstallment['status'];
    /** Derivato dal database sulla data italiana: non ricalcolarlo qui. */
    isOverdue?: boolean;
    /** Addebito differito in volo: pagata dal cliente, non ancora confermata da Stripe. */
    isProcessing?: boolean;
}

/**
 * Colori del badge di stato di una rata.
 *
 * Esportata perche' la tabella delle rate in scadenza mostra lo stesso stato con un testo
 * diverso ("Scaduta da 3 gg" invece di "Scaduta"): non puo' riusare il componente, ma non
 * deve nemmeno riscriversi le classi a mano — l'aveva fatto, e un cambio di palette andava
 * applicato in due punti sperando di ricordarseli entrambi.
 *
 * L'ordine dei casi e' quello del componente e conta: "in corso" batte "scaduta".
 */
export const installmentBadgeClassName = ({
    status,
    isOverdue,
    isProcessing,
}: {
    status: IInstallment['status'];
    isOverdue?: boolean;
    isProcessing?: boolean;
}): string => {
    if (status === 'paid') return 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-100';
    if (status === 'cancelled') return 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-100';
    if (isProcessing) return 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-100';
    if (isOverdue) return 'bg-red-100 text-red-800 border border-red-300 hover:bg-red-100';
    return 'bg-white text-gray-600 border border-gray-200 hover:bg-white';
};

/**
 * Stato di una rata. Ne' "scaduta" ne' "pagamento in corso" sono stati memorizzati: la
 * prima e' status='pending' con scadenza passata, la seconda e' l'esistenza di un
 * tentativo non ancora risolto. Per questo arrivano come flag separati.
 */
export const InstallmentStatusBadge = ({ status, isOverdue, isProcessing }: InstallmentStatusBadgeProps) => {
    const { t } = useI18n();

    // Stesso ordine di installmentBadgeClassName. In particolare "in corso" viene prima di
    // "scaduta": una rata pagata il giorno della scadenza con addebito differito sfora
    // mentre l'incasso e' in arrivo, e mostrarla in rosso sarebbe falso.
    const label =
        status === 'paid'
            ? t('checkout.paymentPlan.status.paid')
            : status === 'cancelled'
            ? t('checkout.paymentPlan.status.cancelled')
            : isProcessing
            ? t('checkout.paymentPlan.status.processing')
            : isOverdue
            ? t('checkout.paymentPlan.status.overdue')
            : t('checkout.paymentPlan.status.pending');

    return (
        <Badge className={installmentBadgeClassName({ status, isOverdue, isProcessing })}>
            {label}
        </Badge>
    );
};
